import { DatePicker, Input, Label } from "@/components/basic";
import { classNames } from "@/lib/classNames";
import { useAuthenticated, useIsDesignSystem2 } from "@gettactic/hooks";
import { addDays } from "date-fns";
import { motion } from "framer-motion";
import { ChevronRightIcon, XIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Frequency, RRule, Weekday } from "rrule";
import { inputV2ClassesOnGray } from "./utils/classes";
import { canScheduleOthers, cn } from "@gettactic/helpers";
import { Button } from "@v2/components/ui/button";

const validPeriodicityOptions: {
  label: string;
  value: AdvancedFormState["freq"];
}[] = [
  { label: "Day", value: RRule.DAILY },
  { label: "Week", value: RRule.WEEKLY },
  { label: "Month", value: RRule.MONTHLY },
  { label: "Year", value: RRule.YEARLY }
];
//

const repeatOnDays: { label: string; value: Weekday }[] = [
  { label: "Sunday", value: RRule.SU },
  { label: "Monday", value: RRule.MO },
  { label: "Tuesday", value: RRule.TU },
  { label: "Wednesday", value: RRule.WE },
  { label: "Thursday", value: RRule.TH },
  { label: "Friday", value: RRule.FR },
  { label: "Saturday", value: RRule.SA }
];

type Props = {
  setAdvanced: React.Dispatch<React.SetStateAction<boolean>>;
  state: AdvancedFormState;
  setState: (x: AdvancedFormState) => void;
  hideWeekends?: boolean;
  minUntilDate: Date;
  isCompact?: boolean;
  showNeverOption?: boolean;
  maxDateAllowed?: Date;
  isCustom?: boolean;
  advanced?: boolean;
  wasNeverEnding?: boolean;
};

export type AdvancedFormState = {
  interval: number;
  freq: Frequency;
  byweekday: Weekday[];
  until: Date | null;
};

export default function ReserveFieldRecurringAdvancedForm({
  setAdvanced,
  state,
  setState,
  hideWeekends,
  minUntilDate,
  isCompact = false,
  showNeverOption = true,
  maxDateAllowed,
  isCustom = false,
  advanced = false,
  wasNeverEnding = false
}: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isV2 = useIsDesignSystem2();
  const isAdmin = canScheduleOthers(authenticatedUser);

  const [untilDate, setUntilDate] = useState(
    state.until ?? (maxDateAllowed || addDays(minUntilDate, 30))
  );

  useEffect(() => {
    if (state.until) {
      setUntilDate(state.until);
    } else if (maxDateAllowed && !state.until) {
      // If no until date is set and we have a maxDateAllowed, use it as default
      setUntilDate(maxDateAllowed);
    }
  }, [state.until, maxDateAllowed]);

  // Debug logging
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("ReserveFieldRecurringAdvancedForm debug:", {
        stateUntil: state.until?.toISOString(),
        maxDateAllowed: maxDateAllowed?.toISOString(),
        untilDate: untilDate.toISOString(),
        showNeverOption
      });
    }
  }, [state.until, maxDateAllowed, untilDate, showNeverOption]);
  
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="relative flex flex-col overflow-hidden rounded-lg bg-[#F7F9FB] p-4 font-inter text-muted-foreground [.tactic-v2_&]:border-1 [.tactic-v2_&]:border-neutral-100"
    >
      {/* Ends on section - always visible when recurring is selected */}
      <div className={advanced ? "order-3" : ""}>
        <div className="flex items-center justify-between">
          <div className={classNames(styles.baseLabelClass, "basis-1/4")}>
            Ends
          </div>
          {!advanced && (
            <Button
              className="flex items-center font-inter text-primary text-sm hover:text-primary-dark"
              variant="ghost"
              onClick={(ev) => {
                ev.preventDefault();
                setAdvanced(true);
              }}
            >
              Options <ChevronRightIcon className="h-4 w-4 rotate-90" />
            </Button>
          )}
        </div>
        <div className="flex flex-col">
          {showNeverOption && (
            <Label radio className="my-1 block">
              <Input
                type="radio"
                onChange={() => {
                  setState({ ...state, until: null });
                }}
                checked={state.until === null}
              />
              <div className="ml-2 text-base">Never</div>
            </Label>
          )}
          <div className={classNames("flex items-center justify-between")}>
            {showNeverOption && (
              <Label
                radio
                className={classNames("block ", isCompact ? "" : "my-1")}
              >
                <Input
                  type="radio"
                  onChange={() => {
                    setState({ ...state, until: untilDate });
                  }}
                  checked={state.until !== null}
                />
                <div className="ml-2 text-base">On</div>
              </Label>
            )}
            <div
              className={classNames(
                "focus-ring-rainbow flex grow basis-3/4",
                isCompact ? "ml-4" : showNeverOption ? "ml-4" : ""
              )}
            >
              <div className="bg-white">
                <DatePicker
                  className={classNames(
                    "input w-full rounded-md text-left bg-white",
                    isV2 ? inputV2ClassesOnGray : "",
                    !state.until && "bg-gray-100 text-gray-400"
                  )}
                  calendarClassName="tailwind-datepicker"
                  dateFormat={"eeee, LLLL d, yyyy"}
                  selected={untilDate}
                  placeholderText={!state.until ? "Never" : ""}
                  onChange={(until) => {
                    setState({ ...state, until });
                  }}
                  minDate={minUntilDate}
                  maxDate={isAdmin ? undefined : maxDateAllowed}
                />
              </div>
                {/* Show "Currently ends: Never" text when appropriate */}
                {wasNeverEnding && !showNeverOption && (
                  <div className="mb-2 ml-3 text-gray-600 text-sm">
                    Currently ends: Never
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced options - only show when advanced is true */}
      {advanced && (
        <>
          <div className="order-1 flex items-center justify-between">
            <h4 className="font-medium text-secondary-bg [.tactic-v2_&]:mb-2 [.tactic-v2_&]:border-[#E5EBEE] [.tactic-v2_&]:font-inter [.tactic-v2_&]:text-[#778086] [.tactic-v2_&]:text-md">
              Options
            </h4>
            <button
              className="text-muted-foreground hover:text-foreground"
              onClick={(ev) => {
                ev.preventDefault();
                setAdvanced(false);
              }}
            >
              <XIcon className="size-4" />
            </button>
          </div>
          <div
            className={classNames(
              "mt-4 flex order-2",
              isCompact ? "flex-col items-start" : "items-center"
            )}
          >
            <div
              className={classNames(
                "font-inter text-sm",
                isCompact ? "flex-1" : "basis-1/4"
              )}
            >
              Repeat every
            </div>
            <div
              className={classNames(
                "flex items-center",
                isCompact ? "w-full items-start gap-1.5" : "gap-3 space-x-1"
              )}
            >
              <div
                className={classNames("", isCompact ? "w-half" : "basis-1/4")}
              >
                <div className="focus-ring-rainbow">
                  <Input
                    onChange={(ev) => {
                      if (ev.currentTarget.valueAsNumber < 0) {
                        return;
                      }
                      setState({
                        ...state,
                        interval: ev.currentTarget.valueAsNumber
                      });
                    }}
                    value={state.interval.toString()}
                    type="number"
                    className={cn(
                      "resource-select h-[42px] rounded-md shadow-none [.v2-reservation_&]:shadow-none ",
                      isV2 ? inputV2ClassesOnGray : ""
                    )}
                  />
                </div>
              </div>

              <div className="focus-ring-rainbow bg-white">
                <Select
                  onChange={(val) => {
                    if (!val) {
                      return;
                    }
                    setState({ ...state, freq: val.value });
                  }}
                  value={validPeriodicityOptions.find(
                    (x) => x.value === state.freq
                  )}
                  className={classNames(
                    "resource-select bg-white",
                    isV2 ? inputV2ClassesOnGray : "",
                    isCompact ? "w-full" : ""
                  )}
                  classNamePrefix="react-select"
                  options={validPeriodicityOptions}
                />
              </div>
            </div>
          </div>
          <div
            className={classNames(
              "mt-2 flex order-2",
              isCompact ? "flex-col items-start mt-4" : "items-center"
            )}
          >
            <div
              className={classNames(
                styles.baseLabelClass,
                isCompact ? "mb-1" : "basis-1/4"
              )}
            >
              Repeat on
            </div>
            <div
              className={classNames(
                "flex items-center",
                isCompact ? "gap-1.5" : "gap-3 space-x-1"
              )}
            >
              {repeatOnDays
                .filter((day) =>
                  hideWeekends
                    ? !(day.value === RRule.SU || day.value === RRule.SA)
                    : true
                )
                .map((day) => {
                  const idx = state.byweekday.findIndex(
                    (x) => x.weekday === day.value.weekday
                  );
                  return (
                    <div
                      key={day.value.toString()}
                      onClick={((idx, value) => () => {
                        const byweekday: Weekday[] = [...state.byweekday];
                        if (idx > -1) {
                          byweekday.splice(idx, 1);
                        } else {
                          byweekday.push(value);
                        }
                        setState({ ...state, byweekday: byweekday });
                      })(idx, day.value)}
                      className={classNames(
                        "font-inter flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border text-center text-[0.9rem] capitalize",
                        idx > -1
                          ? "bg-primary text-white "
                          : "border-neutral-200 text-secondary-bg [.tactic-v2_&]:border-secondary-foreground/20 [.tactic-v2_&]:text-secondary-foreground"
                      )}
                      title={day.label}
                    >
                      {day.label[0]}
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}

const styles = {
  baseLabelClass: "font-inter text-sm"
};
